import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UploadFlowDialogs } from '@/components/upload/UploadFlowDialogs';
import { useProductStats } from '@/hooks/useProductStats';
import { useUploadJob } from '@/hooks/useUploadJob';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';

interface WorkbenchUploadActionProps {
  feedId: string;
  /**
   * Schreibt eine noch offene Handkorrektur weg, BEVOR hochgeladen wird.
   *
   * V2-Befund: der Knopf sitzt auf Seitenebene und hatte keinerlei Verbindung
   * zum Bearbeitungszustand der Karte. Wer im Reiter "Freigegeben" einen Titel
   * korrigierte und dann "Zu Google uebertragen" klickte, schickte den ALTEN
   * Text — waehrend die Karte den neuen ausdruecklich als das auswies, was
   * hochgeladen wuerde (`WorkbenchProductCard`: `after={editState.optimized_title}`).
   * Danach steht `upload_status` auf `uploaded`, den `needs_reupload`-Ruecksetzer
   * gibt es bewusst nicht, und die Korrektur erreicht Google nie.
   *
   * Liefert `false`, wenn das Speichern fehlgeschlagen ist — dann wird NICHT
   * hochgeladen. Fehlt die Angabe (Aufrufer ohne Werkbank), gilt es als nichts
   * zu speichern.
   */
  flushPendingEdits?: () => Promise<boolean>;
  /**
   * Meldet den Oeffner des Upload-Dialogs nach aussen (Spec 2026-09-16,
   * Abschnitt 7; Muster `registerFlushPendingEdits`/`registerBulkRelease` in
   * `Workbench.tsx`).
   *
   * Noetig, weil die Leerzustaende der Werkbank den Upload jetzt selbst
   * anbieten, der `useUploadJob` dieser Seite aber HIER lebt. Eine zweite
   * Instanz im Leerzustand waere eine zweite Dialog-Kette mit eigenem
   * Herkunfts-Gate -- genau die Drift, gegen die `UploadFlowDialogs`
   * extrahiert wurde.
   */
  registerOpenUpload?: (fn: (() => void) | null) => void;
}

/**
 * Der Upload aus der Werkbank heraus.
 *
 * Die Werkbank war bisher eine Sackgasse: sie enthielt keinerlei Upload-
 * Verdrahtung, der einzige Rueckweg war ein Link auf die Startseite. Seit die
 * eine Fortschrittsleiste ihren vierten Schritt auf `/workbench` zeigt, muss
 * dieser Schritt hier auch irgendwohin fuehren.
 *
 * ABWEICHUNG vom Aufgabenbrief (dort: `WorkbenchActionBar`): die Aktion sitzt
 * auf Seitenebene, nicht in der Aktionsleiste der Karte. Die Aktionsleiste
 * rendert nur im Kartenmodus UND nur, solange es ein aktuelles Produkt gibt —
 * `WorkbenchReviewer` steigt vorher bei Laden, Fehler, "nichts zu pruefen" und
 * vor allem bei "alles geprueft" aus. Genau der letzte Fall ist der Moment,
 * in dem alles freigegeben ist und hochgeladen werden will; dort haette der
 * Knopf nie existiert. Seit die Ansichtswahl pro Feed gemerkt wird, faellt
 * zusaetzlich jeder Nutzer im Listenmodus aus der Aktionsleiste heraus.
 *
 * Zwei verschiedene Groessen, mit Absicht:
 *  - SICHTBARKEIT haengt an `approvedAwaitingUpload` — dieselbe Zahl, aus der
 *    die Leiste ihren Schritt 4 ableitet. `approved` allein faellt nach einem
 *    Upload nie wieder auf null (Schnitt 2, Blocker B1), der Knopf stuende
 *    sonst fuer immer da.
 *  - Die ZAHL im Knopf und im Dialog ist `approved`. `approved_only: true`
 *    waehlt serverseitig alle freigegebenen Produkte, auch bereits
 *    hochgeladene (`enqueue-upload-batches` filtert nicht auf
 *    `uploaded_to_gmc_at`). Der Dialog-Vertrag lautet, dass seine Zahlen genau
 *    vorhersagen, was der Server auswaehlt — eine kleinere Zahl waere ein
 *    Versprechen, das der naechste Klick bricht.
 *
 * Kein eigener Auswahl-Bereich (`getUploadSelection`): der Upload aus der
 * Werkbank ist immer feed-weit, die Einschraenkung macht der Dialog ueber
 * `approved_only`. Damit bleibt der bestehende Bereichs-Vertrag von
 * `enqueue-upload-batches` unberuehrt.
 *
 * Bei widerrufenem Merchant-Center-Zugriff ist der Knopf gesperrt und nennt den
 * Grund. `handleUploadButtonClick` lehnt zwar
 * ohnehin ab — aber ein bedienbarer Knopf, der nur eine Fehlermeldung erzeugt,
 * ist eine Sackgasse mit Extraschritt.
 *
 * BEKANNTER UNTERSCHIED zum Dashboard: dort sperrt derselbe Knopf zusaetzlich
 * waehrend eines laufenden Optimierungslaufs (`optimization.isOptimizing`).
 * Diese Sperre ist hier NICHT nachgebaut — sie haengt am
 * `OptimizationProvider`, den `/workbench` nicht mountet. Ein eigener Provider
 * nur fuer eine Knopfsperre waere ein zweiter Lauf-Zustand neben dem
 * bestehenden. Folge: waehrend eine Optimierung laeuft, laesst sich von hier
 * aus hochladen; der Upload nimmt dann den Stand von vorhin mit. Kandidat fuer
 * Task 11 / Phase E.
 */
export function WorkbenchUploadAction({ feedId, flushPendingEdits, registerOpenUpload }: WorkbenchUploadActionProps) {
  const { t } = useTranslation('workbench');
  /* Lang oder kurz beschriftet. Der Unterschied sind rund 130 px, und die
     entscheiden gemessen darueber, ob die einzeilige Kopfzone bei
     ausgeklappter Seitenleiste umbricht. Ausserhalb der Entwurfsseite
     liefert der Haken 'lang', also den heutigen Text. */
  const { uploadBeschriftung } = useEntwurf();
  const uploadKey = uploadBeschriftung === 'kurz' ? 'actions.uploadShort' : 'actions.upload';
  const { stats, refetchStats, statsFeedId } = useProductStats(feedId, false, null, null);
  const upload = useUploadJob(feedId, refetchStats);
  const { isAccessRevoked: isMcAccessRevoked } = useMcAccessStatus();

  // Gehoeren die Zahlen ueberhaupt zu DIESEM Feed? `useProductStats` haelt beim
  // Feed-Wechsel per `placeholderData` die Zahlen des vorherigen Feeds und meldet
  // dabei `isPending === false` — es gibt also kein Ladeflag, das dieses Fenster
  // beschreibt. Hier waere das teurer als anderswo: `enqueue-upload-batches`
  // waehlt ueber `feed_id`, der Knopf traegt aber die Zahl aus `stats`. Ein Klick
  // im Fenster verspricht die freigegebenen Produkte des ALTEN Feeds und schickt
  // die des neuen zu Google — und zurueckholen laesst sich das nicht.
  //
  // Erreichbar wurde der Fall erst durch die globale Feed-Auswahl in der
  // Kopfzeile (Feed-Check-Konsolidierung): vorher musste man die Werkbank
  // verlassen, um den Feed zu wechseln, und der Wechsel remountete diese Flaeche.
  // An- und Abmelden mit dem Lebenszyklus dieses Bauteils: beim Aushaengen
  // wieder `null`, sonst zeigte der Leerzustand auf einen Dialog, der nicht
  // mehr im Baum steht.
  useEffect(() => {
    registerOpenUpload?.(upload.handleUploadButtonClick);
    return () => registerOpenUpload?.(null);
  }, [registerOpenUpload, upload.handleUploadButtonClick]);

  const statsMatchFeed = statsFeedId === feedId;

  const uploadCount = stats.approved;
  const hasWaitingWork = statsMatchFeed && stats.approvedAwaitingUpload > 0;

  // Konvergenz 4, Befund 2: ein Lauf, den NICHT diese Instanz gestartet hat
  // (zweiter Tab, naechtlicher Auto-Upload) darf kein fokusfangendes Modal
  // aufziehen — er wird als unaufdringliche Fortschrittszeile gezeigt.
  const foreignUploadRunning = !!upload.activeUploadJob && !upload.uploadStartedHere;

  return (
    <>
      {(hasWaitingWork || foreignUploadRunning) && (
        <div className="flex flex-col items-start sm:items-end gap-1">
          {hasWaitingWork && (
            <Button
              variant="outline"
              onClick={upload.handleUploadButtonClick}
              disabled={upload.isUploading || isMcAccessRevoked}
              loading={upload.uploadSubmitting}
              className="gap-2"
              // Ein gesperrter Knopf ohne Grund ist eine Sackgasse. `title` traegt
              // ihn fuer die Maus, `aria-label` fuer alles andere — ein gesperrter
              // Knopf ist nicht fokussierbar, `aria-describedby` erreichte dort
              // niemanden.
              title={isMcAccessRevoked ? t('actions.uploadBlockedMcRevoked') : undefined}
              aria-label={
                isMcAccessRevoked
                  ? `${t(uploadKey, { count: uploadCount })}. ${t('actions.uploadBlockedMcRevoked')}`
                  : undefined
              }
            >
              <Upload className="h-4 w-4" />
              {t(uploadKey, { count: uploadCount })}
            </Button>
          )}
          {/* Sichtbarer Sperr-Grund fuer Touch (Konvergenz 4, Befund 6):
              title braucht Hover, aria-label einen Screenreader — ein
              Touch-Nutzer hat beides nicht. */}
          {hasWaitingWork && isMcAccessRevoked && (
            <p className="text-xs text-warning-ink max-w-xs sm:text-right" role="status">
              {t('actions.uploadBlockedMcRevoked')}
            </p>
          )}
          {/* Der Weg zum Fortschritt eines fremden Laufs: live mitlaufende
              Zahlen aus derselben Query, kein Modal. */}
          {foreignUploadRunning && upload.activeUploadJob && (
            <p className="text-xs text-muted-foreground max-w-xs sm:text-right" role="status">
              {t('actions.uploadRunningElsewhere', {
                uploaded: upload.activeUploadJob.uploaded_products.toLocaleString(),
                total: upload.activeUploadJob.total_products.toLocaleString(),
              })}
            </p>
          )}
        </div>
      )}

      {/* Dialog-Verdrahtung (Bestaetigen/Fortschritt/Erfolg) lebt in
          UploadFlowDialogs — geteilt mit dem zweiten Mount-Ort auf der
          Feed-Check-Seite (F-B). Haengt bewusst NICHT an `hasWaitingWork`:
          sobald der Upload durch ist, faellt `approvedAwaitingUpload` auf
          null — der Fortschritts- und der Erfolgsdialog wuerden mitten im
          Lauf verschwinden. */}
      <UploadFlowDialogs
        feedId={feedId}
        upload={upload}
        approvedCount={uploadCount}
        analyticsSource="workbench"
        beforeUpload={flushPendingEdits}
      />
    </>
  );
}
